import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["triggerField", "conditionalRow"]
    static values = {
        debug: {type: Boolean, default: false},
        conditionalRules: {type: Object, default: {}}
    }

    connect() {
        if (this.debugValue) {
            console.log("🚀 CustomAppFormConditional controller connected", {
                triggerFields: this.triggerFieldTargets.length,
                conditionalRows: this.conditionalRowTargets.length,
                rules: this.conditionalRulesValue
            })
        }

        // Reconcile `required` for every conditional row up front so that
        // rows the server rendered as hidden don't keep required-but-hidden
        // inputs (which would block submit with "not focusable"). This
        // covers the case where the trigger field has no initial value and
        // `initializeFieldVisibility` therefore never runs the reconciler.
        this.conditionalRowTargets.forEach(row => {
            this.reconcileRequiredAttributes(row, this.isRowCurrentlyVisible(row))
        })

        // Initialize field visibility on page load
        this.initializeFieldVisibility()

        // Set up mutation observer for dynamic content
        this.setupMutationObserver()

        // Trigger initial custom event
        this.dispatch('controllerConnected', {
            detail: {
                formId: this.element.id,
                triggerFieldsCount: this.triggerFieldTargets.length,
                conditionalRowsCount: this.conditionalRowTargets.length
            }
        })
    }

    disconnect() {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect()
        }

        if (this.debugValue) {
            console.log("🔌 CustomAppFormConditional controller disconnected")
        }
    }

    // ============================================================================
    // MAIN EVENT HANDLERS
    // ============================================================================

    // Observer Pattern: Handle field changes and trigger visibility updates
    handleFieldChange(event) {
        const triggerField = event.target
        const fieldName = this.getFieldName(triggerField)
        const fieldValue = this.getFieldValue(triggerField)

        if (this.debugValue) {
            console.log(`📝 Field changed: ${fieldName} = "${fieldValue}"`)
        }

        // Add visual feedback for trigger field
        this.addTriggerFieldFeedback(triggerField)

        // Update conditional fields with debouncing for better performance
        this.debouncedUpdate(fieldName, fieldValue)
    }

    // ============================================================================
    // INITIALIZATION AND VISIBILITY LOGIC
    // ============================================================================

    // Strategy Pattern: Initialize visibility on page load
    initializeFieldVisibility() {
        // Process each trigger field that has a value
        this.triggerFieldTargets.forEach(field => {
            const fieldName = this.getFieldName(field)
            const fieldValue = this.getFieldValue(field)

            if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
                this.updateConditionalFields(fieldName, fieldValue)
            }
        })

        // Also check for any pre-selected values in dropdowns or radio buttons
        this.element.querySelectorAll('select, input[type="radio"]:checked').forEach(field => {
            if (this.fieldTriggersConditionals(field)) {
                const fieldName = this.getFieldName(field)
                const fieldValue = this.getFieldValue(field)
                if (fieldValue) {
                    this.updateConditionalFields(fieldName, fieldValue)
                }
            }
        })

        // Handle forms with existing data (edit mode)
        this.handleExistingFormData()
    }

    // Handle forms that already have data (edit mode)
    handleExistingFormData() {
        // Find all form fields with values and check if they trigger conditionals
        const formFields = this.element.querySelectorAll('input, select, textarea')
        formFields.forEach(field => {
            if (this.fieldTriggersConditionals(field)) {
                const fieldValue = this.getFieldValue(field)
                if (fieldValue) {
                    const fieldName = this.getFieldName(field)
                    this.updateConditionalFields(fieldName, fieldValue)
                }
            }
        })
    }

    // Command Pattern: Update visibility of conditional fields with performance optimization
    updateConditionalFields(triggerFieldName, triggerValue) {
        const affectedRows = this.conditionalRowTargets.filter(row =>
            row.dataset.conditionalField === triggerFieldName
        )

        if (affectedRows.length === 0) {
            if (this.debugValue) {
                console.log(`ℹ️ No conditional rows found for field: ${triggerFieldName}`)
            }
            return
        }

        if (this.debugValue) {
            console.log(`🔄 Updating ${affectedRows.length} conditional rows for ${triggerFieldName} = "${triggerValue}"`)
        }

        // Batch DOM updates for better performance
        const updates = affectedRows.map(row => ({
            row,
            shouldShow: this.evaluateCondition(row, triggerFieldName, triggerValue)
        }))

        // Apply all updates in a single animation frame
        requestAnimationFrame(() => {
            updates.forEach(({row, shouldShow}) => {
                this.toggleRowVisibility(row, shouldShow)
            })

            // Trigger custom event for other controllers to listen to
            this.dispatch('fieldsUpdated', {
                detail: {
                    triggerField: triggerFieldName,
                    triggerValue: triggerValue,
                    affectedRowsCount: affectedRows.length,
                    visibleRowsCount: updates.filter(u => u.shouldShow).length
                }
            })
        })
    }

    // ============================================================================
    // CONDITION EVALUATION LOGIC
    // ============================================================================

    // Enhanced Strategy Pattern: Evaluate different types of conditions
    evaluateCondition(row, triggerFieldName, triggerValue) {
        const conditionField = row.dataset.conditionalField
        const conditionType = row.dataset.conditionalCondition
        const conditionValue = row.dataset.conditionalValue
        const conditionValues = row.dataset.conditionalValues?.split(',') || []
        const conditionAction = row.dataset.conditionalAction || 'show'

        // Only evaluate if this row depends on the changed field
        if (conditionField !== triggerFieldName) {
            return this.isRowCurrentlyVisible(row) // Keep current state
        }

        let conditionMet = false

        // Convert values to strings for consistent comparison
        const triggerValueStr = String(triggerValue || '').trim()
        const conditionValueStr = String(conditionValue || '').trim()

        switch (conditionType) {
            case 'equals':
                conditionMet = triggerValueStr === conditionValueStr
                break

            case 'not_equals':
                conditionMet = triggerValueStr !== conditionValueStr
                break

            case 'in':
                conditionMet = conditionValues.includes(triggerValueStr)
                break

            case 'not_in':
                conditionMet = !conditionValues.includes(triggerValueStr)
                break

            case 'contains':
                conditionMet = triggerValueStr.toLowerCase().includes(conditionValueStr.toLowerCase())
                break

            case 'not_contains':
                conditionMet = !triggerValueStr.toLowerCase().includes(conditionValueStr.toLowerCase())
                break

            case 'starts_with':
                conditionMet = triggerValueStr.toLowerCase().startsWith(conditionValueStr.toLowerCase())
                break

            case 'ends_with':
                conditionMet = triggerValueStr.toLowerCase().endsWith(conditionValueStr.toLowerCase())
                break

            case 'greater_than':
                const triggerNum = parseFloat(triggerValue)
                const conditionNum = parseFloat(conditionValue)
                conditionMet = !isNaN(triggerNum) && !isNaN(conditionNum) && triggerNum > conditionNum
                break

            case 'less_than':
                const triggerNumLt = parseFloat(triggerValue)
                const conditionNumLt = parseFloat(conditionValue)
                conditionMet = !isNaN(triggerNumLt) && !isNaN(conditionNumLt) && triggerNumLt < conditionNumLt
                break

            case 'greater_than_or_equal':
                const triggerNumGte = parseFloat(triggerValue)
                const conditionNumGte = parseFloat(conditionValue)
                conditionMet = !isNaN(triggerNumGte) && !isNaN(conditionNumGte) && triggerNumGte >= conditionNumGte
                break

            case 'less_than_or_equal':
                const triggerNumLte = parseFloat(triggerValue)
                const conditionNumLte = parseFloat(conditionValue)
                conditionMet = !isNaN(triggerNumLte) && !isNaN(conditionNumLte) && triggerNumLte <= conditionNumLte
                break

            case 'is_empty':
                conditionMet = !triggerValue || triggerValueStr === ''
                break

            case 'is_not_empty':
                conditionMet = triggerValue && triggerValueStr !== ''
                break

            default:
                console.warn(`⚠️ CustomAppFormConditional: Unknown condition type: ${conditionType}`)
                conditionMet = false
        }

        // Handle different actions (show/hide)
        const result = conditionAction === 'hide' ? !conditionMet : conditionMet

        if (this.debugValue) {
            console.log(`🧮 Condition evaluation:`, {
                field: conditionField,
                condition: `${triggerValueStr} ${conditionType} ${conditionValueStr}`,
                conditionMet,
                action: conditionAction,
                finalResult: result
            })
        }

        return result
    }

    // ============================================================================
    // VISIBILITY TOGGLE AND ANIMATIONS
    // ============================================================================

    // Enhanced Template Method: Toggle row visibility with smooth animations
    toggleRowVisibility(row, shouldShow) {
        const isCurrentlyVisible = this.isRowCurrentlyVisible(row)

        // Always reconcile `required` attributes with the desired visibility,
        // even when the display state doesn't need to change. Browser HTML5
        // validation cannot focus a `required` input inside a `display: none`
        // parent and aborts form submission ("not focusable"), so any field
        // hidden by this controller must have its `required` suspended.
        this.reconcileRequiredAttributes(row, shouldShow)

        if (shouldShow === isCurrentlyVisible) {
            return // No display change needed
        }

        if (shouldShow) {
            this.showRow(row)
        } else {
            this.hideRow(row)
            this.clearRowFields(row)
        }
    }

    // Suspend `required` while a row is hidden; restore it when shown.
    // Original state is stashed on `data-suspended-required` so re-show is
    // lossless and only fields originally marked `required` get re-required.
    reconcileRequiredAttributes(row, shouldBeVisible) {
        if (shouldBeVisible) {
            row.querySelectorAll('[data-suspended-required="true"]').forEach(field => {
                field.setAttribute('required', '')
                delete field.dataset.suspendedRequired
            })
        } else {
            row.querySelectorAll('[required]').forEach(field => {
                field.dataset.suspendedRequired = 'true'
                field.removeAttribute('required')
            })
        }
    }

    // Enhanced animation helpers with better performance
    showRow(row) {
        // Reset any ongoing animations
        row.style.transition = ''
        row.style.display = 'flex'
        row.classList.remove('conditional-hidden')
        row.classList.add('conditional-visible')

        // Trigger reflow and animate
        requestAnimationFrame(() => {
            row.style.transition = 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out'
            row.style.opacity = '1'
            row.style.transform = 'translateY(0)'

            // Focus first input if user just triggered this row
            setTimeout(() => {
                const firstInput = row.querySelector('input:not([type="hidden"]), select, textarea')
                if (firstInput && this.shouldFocusOnShow(row)) {
                    firstInput.focus()
                }
            }, 300)
        })

        if (this.debugValue) {
            console.log(`👁️ Showing row:`, row.id || 'unnamed')
        }
    }

    hideRow(row) {
        row.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out'
        row.style.opacity = '0'
        row.style.transform = 'translateY(-10px)'

        setTimeout(() => {
            row.style.display = 'none'
            row.classList.remove('conditional-visible')
            row.classList.add('conditional-hidden')
        }, 200)

        if (this.debugValue) {
            console.log(`🙈 Hiding row:`, row.id || 'unnamed')
        }
    }

    // Enhanced field clearing with validation reset
    clearRowFields(row) {
        const fields = row.querySelectorAll('input, select, textarea')
        fields.forEach(field => {
            // Store original value for potential undo
            if (!field.dataset.originalValue) {
                field.dataset.originalValue = field.value
            }

            if (field.type === 'checkbox' || field.type === 'radio') {
                field.checked = false
            } else {
                field.value = ''
            }

            // Clear validation states
            field.classList.remove('is-invalid', 'is-valid', 'border-red-500', 'border-green-500')

            // Remove any error messages
            const errorElement = field.parentNode.querySelector('.error-message, .field-error')
            if (errorElement) {
                errorElement.remove()
            }

            // Trigger change event for other listeners
            field.dispatchEvent(new Event('change', {bubbles: true}))
        })
    }

    // ============================================================================
    // PERFORMANCE OPTIMIZATIONS
    // ============================================================================

    // Performance optimization: Debounced updates
    debouncedUpdate(fieldName, fieldValue) {
        clearTimeout(this.updateTimeout)
        this.updateTimeout = setTimeout(() => {
            this.updateConditionalFields(fieldName, fieldValue)
        }, 150) // 150ms debounce
    }

    // Visual feedback for trigger fields
    addTriggerFieldFeedback(field) {
        field.classList.add('conditional-trigger-active')
        setTimeout(() => {
            field.classList.remove('conditional-trigger-active')
        }, 500)
    }

    // Setup mutation observer for dynamic content
    setupMutationObserver() {
        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    // Re-initialize when new form fields are added dynamically
                    setTimeout(() => this.initializeFieldVisibility(), 100)
                }
            })
        })

        this.mutationObserver.observe(this.element, {
            childList: true,
            subtree: true
        })
    }

    // ============================================================================
    // UTILITY METHODS
    // ============================================================================

    getFieldName(field) {
        // Handle both Rails-style naming and simple names
        if (field.dataset.triggerField) {
            return field.dataset.triggerField
        }

        // Extract from Rails naming convention: resource[field_name]
        const match = field.name.match(/\[([^\]]+)\]/)
        return match ? match[1] : field.name
    }

    getFieldValue(field) {
        if (field.type === 'checkbox') {
            return field.checked ? field.value : ''
        } else if (field.type === 'radio') {
            const radioGroup = document.querySelectorAll(`input[name="${field.name}"]`)
            const checked = Array.from(radioGroup).find(radio => radio.checked)
            return checked ? checked.value : ''
        } else {
            return field.value
        }
    }

    isRowCurrentlyVisible(row) {
        return row.style.display !== 'none' &&
            !row.classList.contains('conditional-hidden') &&
            row.offsetParent !== null
    }

    fieldTriggersConditionals(field) {
        return field.dataset.customAppFormConditionalTarget === 'triggerField' ||
            field.dataset.triggerField ||
            field.hasAttribute('data-custom-app-form-conditional-target')
    }

    shouldFocusOnShow(row) {
        // Only focus if this was triggered by user interaction, not initialization
        return document.activeElement &&
            this.fieldTriggersConditionals(document.activeElement)
    }

    // ============================================================================
    // PUBLIC API AND DEBUG METHODS
    // ============================================================================

    // Public method for other controllers to refresh conditional fields
    refreshConditionalFields() {
        if (this.debugValue) {
            console.log("🔄 Manually refreshing conditional fields")
        }
        this.initializeFieldVisibility()
    }

    // Public method to get current conditional state
    getConditionalState() {
        return {
            triggerFields: this.triggerFieldTargets.map(field => ({
                name: this.getFieldName(field),
                value: this.getFieldValue(field)
            })),
            conditionalRows: this.conditionalRowTargets.map(row => ({
                id: row.id,
                condition: `${row.dataset.conditionalField} ${row.dataset.conditionalCondition} ${row.dataset.conditionalValue}`,
                visible: this.isRowCurrentlyVisible(row)
            }))
        }
    }

    // Enhanced debug helper
    logConditionalState() {
        console.group('🔍 CustomAppFormConditional State Debug')

        console.log('📊 Summary:', {
            triggerFieldsCount: this.triggerFieldTargets.length,
            conditionalRowsCount: this.conditionalRowTargets.length,
            visibleRowsCount: this.conditionalRowTargets.filter(row => this.isRowCurrentlyVisible(row)).length
        })

        console.group('🎯 Trigger Fields')
        this.triggerFieldTargets.forEach((field, index) => {
            const fieldName = this.getFieldName(field)
            const fieldValue = this.getFieldValue(field)
            console.log(`${index + 1}. ${fieldName} = "${fieldValue}"`)
        })
        console.groupEnd()

        console.group('📋 Conditional Rows')
        this.conditionalRowTargets.forEach((row, index) => {
            const visible = this.isRowCurrentlyVisible(row)
            const condition = `${row.dataset.conditionalField} ${row.dataset.conditionalCondition} ${row.dataset.conditionalValue}`
            console.log(`${index + 1}. ${visible ? '✅' : '❌'} ${condition}`)
        })
        console.groupEnd()

        console.groupEnd()
    }

    // Global debug method (can be called from browser console)
    static debugAllInstances() {
        const controllers = document.querySelectorAll('[data-controller*="custom-app-form-conditional"]')
        controllers.forEach((element, index) => {
            console.log(`🎛️ Controller ${index + 1}:`)
            const stimulus = element.stimulus || element._stimulus
            if (stimulus) {
                const controller = stimulus.controllers.find(c => c.identifier === 'custom-app-form-conditional')
                if (controller) {
                    controller.logConditionalState()
                }
            }
        })
    }
}

// Global helper for debugging (available in browser console)
window.debugConditionalForms = () => {
    import('./custom_app_form_conditional_controller').then(module => {
        module.default.debugAllInstances()
    })
}